import { useContext, useEffect, useState } from 'react'

import { context, setAnytimeToday, setServices } from '../../../../store'
import { fields, slots } from '../../../../store/constants'
import { loadBookingState, saveBookingState } from '../../../../store/booking-persistence'
import { formDraft } from '../../../../lib/booking-draft'
import { draftCarLocation } from '../../../../lib/car-location'
import { CAR_LOOKUP, SIZE_SOURCE } from '../../../../lib/vehicle'
import { AnalyticsService } from '../../../../services/analytics'
import { STEPS } from '../steps'

// The whole Book & pay form, kept across a reload, a dropped background tab,
// a trip to Stripe's page and back, or a return later the same day
// (lib/booking-draft.js; Robin, 9 Oct 2026). Put back once the prices are in
// (the wash and extras are service ids), then written down on every change.
//
// A link that names a wash (?service=) still decides the wash: StepBook
// applies it after this, over the draft's, and the extras chosen stay. Not on
// a reload or a Back to the same link, where the draft is what the visitor
// last chose (StepBook's linkWins; `servicesRestored` says a draft had one).
export const navigationType = () => {
  try { return window.performance.getEntriesByType('navigation')[0]?.type || 'navigate' } catch (e) { return 'navigate' }
}

// onRestore(name) runs for each form field the draft puts back.
export const useBookDraft = ({ bookingForm, pricesLoaded, watchedKey, onRestore }) => {
  const { state, dispatch } = useContext(context)
  const [restored, setRestored] = useState(false)
  const [servicesRestored, setServicesRestored] = useState(false)

  useEffect(() => {
    if (restored || !pricesLoaded) return
    const saved = loadBookingState()
    if (saved && saved.step === STEPS.BOOK && saved.redirectedAt) {
      // Back from Stripe's page without paying: its cancel link loads this
      // page afresh (cancelled), the browser's Back is a back_forward load.
      AnalyticsService.trackStripeCheckoutReturned(navigationType() !== 'back_forward')
    }
    if (saved && saved.step === STEPS.BOOK) {
      if (saved.services && saved.servicesAvailability) {
        dispatch(setServices({ services: saved.services, servicesAvailability: saved.servicesAvailability }))
        setServicesRestored(true)
      }
      const form = saved.form || {}
      const put = (name, value) => {
        if (value === undefined || value === null || value === '') return
        bookingForm.setValue(name, value)
        if (onRestore) onRestore(name)
      }
      put(fields.carSize, form.carSize)
      if (form.congestionZone) put(fields.congestionZone, true)
      put(fields.notes, form.notes)
      put(fields.postcode, form.postcode)
      put(fields.address, form.address)
      // The Car location box (lib/car-location.js): what it showed, and
      // whether that was a picked address or typed. A draft from the old
      // postcode and house number boxes comes back as one Car location.
      put(fields.carLocation, draftCarLocation(form))
      if (form.addressPicked) bookingForm.setValue(fields.addressPicked, true)
      if (form.locationManual) bookingForm.setValue(fields.locationManual, true)
      put(fields.mobile, form.mobile)
      // The looked-up car first (not a field, so no onRestore), then its
      // plate: the Registration box shows it again without asking.
      if (form.carLookup) bookingForm.setValue(CAR_LOOKUP, form.carLookup)
      // Form v3: where the size came from (the plate, or picked).
      if (form.sizeSource) bookingForm.setValue(SIZE_SOURCE, form.sizeSource)
      put(fields.carRegistration, form.carRegistration)
      // usableDraft has dropped a day that can no longer be booked; then the
      // preselected tomorrow and the default slot stand.
      if (form.date) {
        put(fields.date, form.date)
        put(fields.timeSlot, form.timeSlot)
        if (form.timeSlot === slots.today) dispatch(setAnytimeToday(true))
      }
    }
    setRestored(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricesLoaded, restored])

  // Back from Stripe into a page the browser kept whole (back-forward
  // cache): nothing reloads, so count the return here.
  useEffect(() => {
    const onPageShow = e => {
      if (!e.persisted) return
      const saved = loadBookingState()
      if (saved && saved.step === STEPS.BOOK && saved.redirectedAt) {
        AnalyticsService.trackStripeCheckoutReturned(false)
        saveBookingState(state, { step: STEPS.BOOK, form: formDraft(bookingForm.getValues()) })
      }
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  })

  useEffect(() => {
    if (!restored) return
    saveBookingState(state, { step: STEPS.BOOK, form: formDraft(bookingForm.getValues()) })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restored, watchedKey, state.services, state.agentTip, state.promoCode])

  return { restored, servicesRestored }
}
